/**
 * @fileoverview React Query hooks for Bank Connections.
 * @module features/bank-connections/hooks/useBankConnections
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  listBankConnections,
  listBanks,
  startBankAuthorization,
  revokeBankConnection,
  syncBankConnection,
} from '../api/bankConnectionsService'
import type {
  BankConnectionDto,
  BankDto,
  BankAuthStartRequest,
  BankAuthStartResponse,
  SyncResultDto,
} from '../schemas/bankConnectionsSchema'

/** Query key for bank connections list. */
export const BANK_CONNECTIONS_QUERY_KEY = ['bank-connections'] as const

/** Query key factory for banks list by country. */
export const banksByCountryQueryKey = (country: string): readonly string[] =>
  ['bank-connections', 'banks', country] as const

/**
 * Hook to fetch all bank connections for the current organization.
 *
 * @returns Query result with bank connections array
 */
export function useBankConnections(): UseQueryResult<BankConnectionDto[], Error> {
  return useQuery({
    queryKey: BANK_CONNECTIONS_QUERY_KEY,
    queryFn: listBankConnections,
    staleTime: 30_000, // 30 seconds
  })
}

/**
 * Hook to fetch available banks for a specific country.
 *
 * @param country ISO 3166-1 alpha-2 country code
 * @param enabled Whether to enable the query
 * @returns Query result with banks array
 */
export function useBanks(
  country: string,
  enabled = true
): UseQueryResult<BankDto[], Error> {
  return useQuery({
    queryKey: banksByCountryQueryKey(country),
    queryFn: () => listBanks(country),
    enabled: enabled && country.length === 2,
    staleTime: 5 * 60_000, // 5 minutes - banks list doesn't change often
  })
}

/**
 * Hook to start bank authorization flow.
 *
 * @returns Mutation result for starting authorization
 */
export function useStartBankAuthorization(): UseMutationResult<
  BankAuthStartResponse,
  Error,
  BankAuthStartRequest
> {
  return useMutation({
    mutationFn: startBankAuthorization,
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start bank authorization')
    },
  })
}

/**
 * Hook to revoke (disconnect) a bank connection.
 *
 * @returns Mutation result for revoking connection
 */
export function useRevokeBankConnection(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: revokeBankConnection,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BANK_CONNECTIONS_QUERY_KEY })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to disconnect bank connection')
    },
  })
}

/** Query key for payments - used to invalidate after sync. */
const PAYMENTS_QUERY_KEY = ['payments'] as const

/** Parameters for syncing a single connection with date range. */
export interface SyncConnectionParams {
  id: string
  dateFrom?: string
  dateTo?: string
}

/**
 * Hook to sync transactions for a specific bank connection.
 *
 * @returns Mutation result for syncing connection
 */
export function useSyncBankConnection(): UseMutationResult<
  SyncResultDto,
  Error,
  SyncConnectionParams
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dateFrom, dateTo }: SyncConnectionParams) => {
      const params: { dateFrom?: string; dateTo?: string } = {}
      if (dateFrom !== undefined) params.dateFrom = dateFrom
      if (dateTo !== undefined) params.dateTo = dateTo
      return syncBankConnection(id, params)
    },
    onSuccess: async (result) => {
      const message = result.message || `Synced ${result.recordsProcessed ?? 0} transactions`
      toast.success(message)
      // Invalidate bank connections and payments cache
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: BANK_CONNECTIONS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: PAYMENTS_QUERY_KEY }),
      ])
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to sync transactions')
    },
  })
}
